import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../../api/client";
import { useAppDialog } from "../../../components/AppDialogProvider";
import AccountSettingSelect from "../../../components/AccountSettingSelect";
import CardAttributePill, { ConditionPill, LanguagePill } from "../../../components/CardAttributePill";
import { renderEditionOption } from "../../../components/dropdownOptionRenderers";
import { formatCustomPriceTrendComparison, getCustomPriceTrendComparison } from "../../../utils/customPriceTrendComparison";
import normalizeCondition from "../../../utils/normalizeCondition";
import { WANTS_ANY_EDITION_VALUE, WANTS_ANY_VALUE, WANTS_BOOLEAN_OPTIONS } from "../../../wantsSettingsOptions";
import { canChooseFoilOption, isFoilOptionDisabled, normalizeFoilSelection } from "./foilOptionRules";
import { getDefaultListingMode, getResolvedListingOption } from "../listingPricing";

const CONDITION_OPTIONS = ["M", "NM", "EX", "GD", "LP", "PL", "PO"];
const BOOLEAN_OPTIONS = [
  { value: "false", label: "No" },
  { value: "true", label: "Yes" }
];
const CURRENT_EDITION_OPTION = "__current_edition__";

const languageToCountry = {
  en: "gb",
  gb: "gb",
  de: "de",
  ger: "de",
  fr: "fr",
  fra: "fr",
  es: "es",
  spa: "es",
  it: "it",
  ita: "it",
  jp: "jp",
  jpn: "jp",
  cn: "cn",
  chi: "cn",
  zho: "cn",
  kr: "kr",
  kor: "kr",
  ru: "ru",
  rus: "ru",
  pt: "pt",
  por: "pt"
};

const conditionColors = {
  M: "#1fc9a4",
  NM: "#4caf50",
  EX: "#6da13d",
  GD: "#eeff00",
  LP: "#ee9a1d",
  PL: "#d65f30",
  PO: "#fa3c2e"
};

const compactLabelStyle = {
  height: "20px",
  borderRadius: "6px",
  boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
};

const DEFAULT_SORT = {
  key: null,
  direction: "asc"
};

const SORTABLE_COLUMN_DEFAULT_DIRECTIONS = {
  qty: "desc",
  name: "asc",
  edition: "asc",
  language: "asc",
  condition: "asc",
  foil: "asc",
  altered: "asc",
  signed: "asc",
  price: "desc",
  trend: "desc",
  foilTrend: "desc"
};

const MOBILE_TEXT_TABLE_BREAKPOINT = 780;

export { DEFAULT_SORT, SORTABLE_COLUMN_DEFAULT_DIRECTIONS };

const sortButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  width: "100%",
  border: "none",
  background: "transparent",
  padding: 0,
  color: "#0f172a",
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer",
  textAlign: "left"
};

const sortIndicatorStyle = {
  minWidth: "12px",
  color: "#64748b",
  fontWeight: 800
};

function formatCurrency(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return `${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function getDisplayName(card) {
  return card.card || card.name || "-";
}

function renderCardName(card) {
  const cardName = getDisplayName(card);

  if (!card?.browseCardPath || cardName === "-") {
    return cardName;
  }

  return (
    <Link
      to={card.browseCardPath}
      onClick={(event) => event.stopPropagation()}
      style={{ color: "#0f172a", textDecoration: "underline" }}
    >
      {cardName}
    </Link>
  );
}

function getDisplayEdition(card) {
  if (card.any_edition || card.anyEdition) {
    return "Any";
  }

  return card.edition || "-";
}

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function getEditionCode(card) {
  return card.edition ? String(card.edition).toUpperCase() : "-";
}

function getRarityColor(rarity) {
  const normalizedRarity = String(rarity || "").toLowerCase();

  if (normalizedRarity.includes("mythic")) return "#d97706";
  if (normalizedRarity.includes("rare")) return "#d4af37";
  if (normalizedRarity.includes("uncommon")) return "#9ca3af";
  if (normalizedRarity.includes("special") || normalizedRarity.includes("bonus")) return "#3b82f6";

  return "#000000";
}

function getDisplayLanguage(card) {
  if (card.any_language || card.anyLanguage) {
    return (
      <span
        title="Any language"
        style={{
          ...compactLabelStyle,
          minWidth: "22px",
          padding: "0 5px",
          fontSize: "11px",
          fontWeight: 700,
          background: "#e2e8f0",
          color: "#334155",
          boxShadow: "none",
          textAlign: "center"
        }}
      >
        Any
      </span>
    );
  }

  if (!card.language) {
    return "-";
  }

  return <LanguagePill language={card.language} />;
}

function getDisplayCondition(card) {
  if (card.any_condition || card.anyCondition) {
    return <ConditionPill any anyLabel="Any condition" />;
  }

  const normalizedCondition = normalizeCondition(card.condition);

  if (!normalizedCondition) {
    return "-";
  }

  return <ConditionPill condition={normalizedCondition} />;
}

function getDisplayPrice(card) {
  return card.sale_price ?? card.user_price ?? null;
}

function getListingMode(card, listingOptions) {
  return getResolvedListingOption(card, listingOptions).mode;
}

function getCustomListingPrice(card, listingOptions) {
  return getResolvedListingOption(card, listingOptions).customPrice || "";
}

function getDisplayFoilTrend(card) {
  if (!card.price_foil) {
    return "— €";
  }

  return formatCurrency(card.price_foil);
}

function getSortableCardValue(card, key) {
  switch (key) {
    case "qty":
      return Number(card?.qty) || 0;
    case "name":
      return getDisplayName(card).toLowerCase();
    case "edition":
      return getDisplayEdition(card).toLowerCase();
    case "language":
      return String(card?.any_language || card?.anyLanguage ? "any" : (card?.language || "")).toLowerCase();
    case "condition":
      return String(card?.any_condition || card?.anyCondition ? "any" : normalizeCondition(card?.condition) || "").toLowerCase();
    case "foil":
      return card?.any_foil || card?.anyFoil ? -1 : (card?.foil ? 1 : 0);
    case "altered":
      return card?.any_altered || card?.anyAltered ? -1 : (card?.altered ? 1 : 0);
    case "signed":
      return card?.any_signed || card?.anySigned ? -1 : (card?.signed ? 1 : 0);
    case "price":
      return Number(getDisplayPrice(card)) || 0;
    case "trend":
      return Number(card?.price_trend) || 0;
    case "foilTrend":
      return Number(card?.price_foil) || 0;
    default:
      return "";
  }
}

function compareSortableValues(left, right) {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base"
  });
}

function getMatchingPrinting(card, printings) {
  const normalizedEdition = normalizeLookupValue(card.edition);
  const normalizedCollectorNumber = normalizeLookupValue(card.collector_number);

  return printings.find((printing) => (
    normalizeLookupValue(printing.setCode) === normalizedEdition
      && normalizeLookupValue(printing.collectorNumber) === normalizedCollectorNumber
  )) || printings.find((printing) => normalizeLookupValue(printing.setCode) === normalizedEdition)
    || printings.find((printing) => (
      normalizeLookupValue(printing.edition) === normalizedEdition
        && normalizeLookupValue(printing.collectorNumber) === normalizedCollectorNumber
    ))
    || printings.find((printing) => normalizeLookupValue(printing.edition) === normalizedEdition)
    || null;
}

function getResolvedEditValues(card, currentEdit, printings) {
  const matchingPrinting = currentEdit.scryfallId
    ? printings.find((printing) => printing.scryfallId === currentEdit.scryfallId) || getMatchingPrinting(card, printings)
    : getMatchingPrinting(card, printings);
  const anyEdition = currentEdit.anyEdition ?? Boolean(card.any_edition ?? card.anyEdition);
  const normalizedFoilState = normalizeFoilSelection({
    foil: currentEdit.foil ?? Boolean(card.foil),
    anyFoil: currentEdit.anyFoil ?? Boolean(card.any_foil ?? card.anyFoil)
  }, {
    selectedPrinting: matchingPrinting || card,
    printings,
    anyEdition
  });

  return {
    qty: Math.max(1, Number(currentEdit.qty) || Number(card.qty) || 1),
    edition: currentEdit.edition ?? matchingPrinting?.setCode ?? card.edition ?? "",
    scryfallId: currentEdit.scryfallId ?? matchingPrinting?.scryfallId ?? "",
    language: currentEdit.language ?? card.language ?? "",
    condition: currentEdit.condition ?? normalizeCondition(card.condition) ?? "",
    foil: normalizedFoilState.foil,
    altered: currentEdit.altered ?? Boolean(card.altered),
    signed: currentEdit.signed ?? Boolean(card.signed),
    anyEdition,
    anyLanguage: currentEdit.anyLanguage ?? Boolean(card.any_language ?? card.anyLanguage),
    anyCondition: currentEdit.anyCondition ?? Boolean(card.any_condition ?? card.anyCondition),
    anyFoil: normalizedFoilState.anyFoil,
    anyAltered: currentEdit.anyAltered ?? Boolean(card.any_altered ?? card.anyAltered),
    anySigned: currentEdit.anySigned ?? Boolean(card.any_signed ?? card.anySigned)
  };
}

function getBooleanSelectValue(value) {
  return value ? "true" : "false";
}

function getBooleanFromSelectValue(value) {
  return value === "true";
}

function renderLanguageValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any</span>;
  }

  if (!value) {
    return <span className="account-setting-select-preview-label">Language</span>;
  }

  const countryCode = languageToCountry[String(value).toLowerCase()] || "xx";

  return (
    <>
      <span className={`fi fi-${countryCode} account-setting-select-preview-flag`} aria-hidden="true" />
      <span className="account-setting-select-preview-label">{String(value).toLowerCase() === "en" ? "English" : (
        String(value).toLowerCase() === "de" ? "German" : (
          String(value).toLowerCase() === "fr" ? "French" : (
            String(value).toLowerCase() === "es" ? "Spanish" : (
              String(value).toLowerCase() === "it" ? "Italian" : (
                String(value).toLowerCase() === "jp" ? "Japanese" : (
                  String(value).toLowerCase() === "cn" ? "Chinese" : (
                    String(value).toLowerCase() === "kr" ? "Korean" : (
                      String(value).toLowerCase() === "ru" ? "Russian" : "Portuguese"
                    )
                  )
                )
              )
            )
          )
        )
      )}</span>
    </>
  );
}

function renderConditionValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any</span>;
  }

  if (!value) {
    return <span className="account-setting-select-preview-label">Condition</span>;
  }

  const normalizedCondition = normalizeCondition(value);

  return (
    <>
      <span
        className="account-setting-select-preview-badge"
        style={{
          background: conditionColors[normalizedCondition] || "#777",
          color: normalizedCondition === "GD" ? "#111" : "#fff"
        }}
      >
        {normalizedCondition}
      </span>
      <span className="account-setting-select-preview-label">{normalizedCondition}</span>
    </>
  );
}

function renderBooleanValue(value, options) {
  return <span className="account-setting-select-preview-label">{options.find((option) => option.value === value)?.label || value}</span>;
}

function getWantBooleanSelectValue(value, anyValue) {
  return anyValue ? WANTS_ANY_VALUE : getBooleanSelectValue(value);
}

function getWantBooleanFormState(value) {
  if (value === WANTS_ANY_VALUE) {
    return { anyValue: true, boolValue: false };
  }

  return {
    anyValue: false,
    boolValue: getBooleanFromSelectValue(value)
  };
}

function getDisplayBoolean(value, anyValue = false, type = "foil") {
  if (anyValue) {
    return (
      <span
        title="Any"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "22px",
          height: "20px",
          padding: "0 5px",
          borderRadius: "6px",
          background: "#e2e8f0",
          color: "#334155",
          fontSize: "11px",
          fontWeight: 700
        }}
      >
        Any
      </span>
    );
  }

  if (!value) {
    return "-";
  }

  return <CardAttributePill type={type} title={type === "foil" ? "Foil" : type === "altered" ? "Altered" : "Signed"} />;
}

function getSelectionBadge() {
  return (
    <div
      style={{
        background: "#1976d2",
        color: "white",
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        fontWeight: "bold"
      }}
      aria-label="Selected"
      title="Selected"
    >
      ✓
    </div>
  );
}

export default function CardsTable({
  cards,
  selectedCards,
  toggleSelect,
  showSelectionIndicatorColumn = false,
  onCardClick,
  isCardDisabled,
  renderRowAction,
  badgeType,
  userId,
  listingOptions = {},
  setListingOptions,
  editMode = false,
  pendingEdits = {},
  setPendingEdits,
  savingEditIds = {},
  onAutoSaveEdit,
  searchApiBasePath = "/api/traders",
  priceColumnLabel = "Price",
  pricePanelTitle = "Listing price",
  emptyPriceCellLabel = "Select row",
  onEditCard,
  sortConfig: controlledSortConfig,
  onSortChange: onControlledSortChange,
  getDisplayQty
}) {
  const { showAlert } = useAppDialog();
  const [setMetadata, setSetMetadata] = useState({});
  const [printingsByCardName, setPrintingsByCardName] = useState({});
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const [isMobileLayout, setIsMobileLayout] = useState(() => (typeof window !== "undefined"
    ? window.innerWidth <= MOBILE_TEXT_TABLE_BREAKPOINT
    : false));
  const usesModalEdit = editMode && typeof onEditCard === "function";
  const isControlledSort = Boolean(controlledSortConfig && onControlledSortChange);
  const effectiveSortConfig = isControlledSort ? controlledSortConfig : sortConfig;
  const hasTradeLockColumn = cards.some((card) => Boolean(card?.tradeLockLabel));
  const resolveDisplayQty = useCallback((card) => {
    if (typeof getDisplayQty === "function") {
      return getDisplayQty(card);
    }

    if (Number.isFinite(Number(card?.displayQty))) {
      return Number(card.displayQty);
    }

    return Math.max(1, Number(card?.qty) || 1);
  }, [getDisplayQty]);

  const handleSortChange = useCallback((key) => {
    const nextSortConfig = ((currentSort) => {
      if (currentSort.key === key) {
        return {
          key,
          direction: currentSort.direction === "asc" ? "desc" : "asc"
        };
      }

      return {
        key,
        direction: SORTABLE_COLUMN_DEFAULT_DIRECTIONS[key] || "asc"
      };
    })(effectiveSortConfig || DEFAULT_SORT);

    if (isControlledSort) {
      onControlledSortChange(nextSortConfig);
      return;
    }

    setSortConfig(nextSortConfig);
  }, [effectiveSortConfig, isControlledSort, onControlledSortChange]);

  const getSortIndicator = useCallback((key) => {
    if (effectiveSortConfig.key !== key) {
      return "";
    }

    return effectiveSortConfig.direction === "asc" ? "↑" : "↓";
  }, [effectiveSortConfig.direction, effectiveSortConfig.key]);

  const getAriaSort = useCallback((key) => {
    if (effectiveSortConfig.key !== key) {
      return "none";
    }

    return effectiveSortConfig.direction === "asc" ? "ascending" : "descending";
  }, [effectiveSortConfig.direction, effectiveSortConfig.key]);

  const sortedCards = useMemo(() => {
    if (isControlledSort || !effectiveSortConfig.key) {
      return cards;
    }

    const directionMultiplier = effectiveSortConfig.direction === "asc" ? 1 : -1;

    return [...cards].sort((leftCard, rightCard) => {
      const primaryComparison = compareSortableValues(
        getSortableCardValue(leftCard, effectiveSortConfig.key),
        getSortableCardValue(rightCard, effectiveSortConfig.key)
      );

      if (primaryComparison !== 0) {
        return primaryComparison * directionMultiplier;
      }

      return compareSortableValues(
        getSortableCardValue(leftCard, "name"),
        getSortableCardValue(rightCard, "name")
      );
    });
  }, [cards, effectiveSortConfig.direction, effectiveSortConfig.key, isControlledSort]);

  const renderSortableHeader = useCallback((label, key) => (
    <button type="button" style={sortButtonStyle} onClick={() => handleSortChange(key)}>
      <span>{label}</span>
      <span style={sortIndicatorStyle}>{getSortIndicator(key)}</span>
    </button>
  ), [getSortIndicator, handleSortChange]);

  const loadPrintingsForCardName = useCallback(async (cardName) => {
    if (!cardName || cardName === "-") {
      return;
    }

    const existingState = printingsByCardName[cardName];

    if (existingState?.isLoading || existingState?.options?.length > 0) {
      return;
    }

    setPrintingsByCardName((current) => ({
      ...current,
      [cardName]: {
        options: current[cardName]?.options || [],
        isLoading: true,
        error: ""
      }
    }));

    try {
      const response = await apiFetch(
        `${searchApiBasePath}/search/prints?card=${encodeURIComponent(cardName)}`,
        { userId }
      );
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || "Could not load editions");
      }

      setPrintingsByCardName((current) => ({
        ...current,
        [cardName]: {
          options: Array.isArray(json.prints) ? json.prints : [],
          isLoading: false,
          error: ""
        }
      }));
    } catch (error) {
      setPrintingsByCardName((current) => ({
        ...current,
        [cardName]: {
          options: current[cardName]?.options || [],
          isLoading: false,
          error: error.message || "Could not load editions"
        }
      }));
    }
  }, [printingsByCardName, searchApiBasePath, userId]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_TEXT_TABLE_BREAKPOINT}px)`);

    const handleMediaQueryChange = (event) => {
      setIsMobileLayout(event.matches);
    };

    setIsMobileLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => mediaQuery.removeEventListener("change", handleMediaQueryChange);
    }

    mediaQuery.addListener(handleMediaQueryChange);
    return () => mediaQuery.removeListener(handleMediaQueryChange);
  }, []);

  useEffect(() => {
    const setCodes = [...new Set(cards
      .map((card) => normalizeLookupValue(card.edition))
      .filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    Promise.all([
      apiFetch(`${searchApiBasePath}/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId })
    ]).then(async ([response]) => {
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not fetch set metadata");
      }

      if (isCancelled) {
        return;
      }

      setSetMetadata((current) => ({
        ...current,
        ...(data.sets || {})
      }));
    }).catch((error) => {
      console.error("Set metadata error:", error);
    });

    return () => {
      isCancelled = true;
    };
  }, [cards, searchApiBasePath, setMetadata, userId]);

  if (isMobileLayout && !editMode) {
    return (
      <div
        style={{
          overflow: "hidden",
          border: "1px solid #d7dce5",
          borderRadius: "12px",
          background: "white",
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)"
        }}
      >
        {sortedCards.map((card, index) => {
          const cardDisabled = Boolean(isCardDisabled?.(card));
          const isSelected = selectedCards?.includes(card.id);
          const resolvedBadgeType = typeof badgeType === "function" ? badgeType(card) : badgeType;
          const isUnlistedTable = resolvedBadgeType === "UNLISTED";
          const isListedTable = resolvedBadgeType === "LISTED";
          const isWantTable = resolvedBadgeType === "WANT";
          const isLockedListedCard = isListedTable && Boolean(card.isTradeLocked);
          const rowAction = renderRowAction ? renderRowAction({ card, cardDisabled }) : null;
          const rowBackground = isListedTable
            ? (isSelected ? "#dff3e3" : "#f2fbf4")
            : isWantTable
              ? (isSelected ? "#e6f4ea" : "#f8fcf9")
              : (isSelected ? "#e8f0fe" : "white");
          const showsListingControls = !editMode && isSelected && (isUnlistedTable || (isListedTable && !isLockedListedCard));
          const displayPrice = getDisplayPrice(card);
          const listingMode = getListingMode(card, listingOptions);
          const cardSetCode = isWantTable && (card.any_edition || card.anyEdition)
            ? ""
            : normalizeLookupValue(card.edition);
          const cardSetMetadata = setMetadata[cardSetCode];
          const setSymbolColor = getRarityColor(card.rarity);
          const updateListingOption = (changes) => {
            if (!setListingOptions) {
              return;
            }

            setListingOptions((prev) => ({
              ...prev,
              [card.id]: {
                mode: getDefaultListingMode(card),
                customPrice: "",
                ...prev[card.id],
                ...changes
              }
            }));
          };

          return (
            <div
              key={card.id}
              onClick={usesModalEdit
                ? (() => onEditCard(card))
                : (onCardClick ? (() => !cardDisabled && onCardClick(card)) : (() => toggleSelect && toggleSelect(card.id)))}
              style={{
                padding: "12px",
                borderBottom: index === sortedCards.length - 1 ? "none" : "1px solid #eef2f7",
                background: cardDisabled ? "#f8fafc" : rowBackground,
                cursor: cardDisabled ? "not-allowed" : ((onCardClick || toggleSelect || usesModalEdit) ? "pointer" : "default"),
                opacity: cardDisabled ? 0.6 : 1,
                boxShadow: isSelected ? "inset 0 0 0 2px #1976d2" : "none"
              }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto auto", gap: "10px", alignItems: "center", minWidth: 0 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "0",
                    height: "auto",
                    padding: 0,
                    borderRadius: 0,
                    background: "transparent",
                    color: "#0f172a",
                    fontSize: "12px",
                    fontWeight: 600
                  }}
                >
                  {resolveDisplayQty(card)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#0f172a",
                      lineHeight: 1.25,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word"
                    }}
                  >
                    {renderCardName(card)}
                  </div>
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "4px",
                    padding: "3px 8px",
                    borderRadius: "999px",
                    background: "#4caf50",
                    color: "white",
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1.35,
                    whiteSpace: "nowrap"
                  }}
                >
                  {displayPrice !== null ? formatCurrency(displayPrice) : emptyPriceCellLabel}
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", minWidth: "26px" }}>
                  {rowAction || (!showSelectionIndicatorColumn && isSelected ? getSelectionBadge() : null)}
                </div>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginTop: "10px", minWidth: 0 }}>
                {cardSetMetadata?.iconSvgMarkup ? (
                  <span
                    aria-hidden="true"
                    title={cardSetMetadata?.name || card.edition || "Unknown edition"}
                    style={{
                      width: "16px",
                      height: "16px",
                      flex: "0 0 auto",
                      color: setSymbolColor,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                    dangerouslySetInnerHTML={{ __html: cardSetMetadata.iconSvgMarkup }}
                  />
                ) : null}
                {getDisplayLanguage(card)}
                {getDisplayCondition(card)}
                {getDisplayBoolean(card.foil, card.any_foil || card.anyFoil, "foil")}
                {getDisplayBoolean(card.altered, card.any_altered || card.anyAltered, "altered")}
                {getDisplayBoolean(card.signed, card.any_signed || card.anySigned, "signed")}
              </div>

              {card.tradeLockLabel ? (
                <div style={{ marginTop: "10px" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "3px 8px",
                      borderRadius: "999px",
                      background: card.isPendingTradeLocked ? "#fff7ed" : "#eff6ff",
                      border: card.isPendingTradeLocked ? "1px solid #fdba74" : "1px solid #93c5fd",
                      color: card.isPendingTradeLocked ? "#9a3412" : "#1d4ed8",
                      fontSize: "9px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {card.tradeLockLabel}
                  </span>
                </div>
              ) : null}

              {showsListingControls ? (
                <div
                  onClick={(event) => event.stopPropagation()}
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    borderRadius: "10px",
                    background: isListedTable ? "#dcf5df" : "#eef4ff"
                  }}
                >
                  <div style={{ fontSize: "10px", lineHeight: 1.35, color: isWantTable ? "#0f766e" : "#14532d", fontWeight: 700, marginBottom: "4px" }}>
                    {pricePanelTitle}
                  </div>
                  <label style={{ display: "block", fontSize: "10px", lineHeight: 1.35, color: isWantTable ? "#0f766e" : "#14532d" }}>
                    <input
                      type="radio"
                      name={`priceMode-${card.id}`}
                      checked={listingMode === "trend"}
                      disabled={Boolean(card.foil)}
                      onChange={() => !card.foil && updateListingOption({ mode: "trend", customPrice: "" })}
                    />
                    {" "}Trend: {formatCurrency(card.price_trend)}
                  </label>
                  <label
                    style={{
                      display: "block",
                      marginTop: "4px",
                      fontSize: "10px",
                      lineHeight: 1.35,
                      color: card.foil && Number(card.price_foil) > 0 ? (isWantTable ? "#0f766e" : "#14532d") : "#6b7280"
                    }}
                  >
                    <input
                      type="radio"
                      name={`priceMode-${card.id}`}
                      checked={listingMode === "foiltrend"}
                      disabled={!card.foil || Number(card.price_foil) <= 0}
                      onChange={() =>
                        card.foil && Number(card.price_foil) > 0 && updateListingOption({ mode: "foiltrend", customPrice: "" })
                      }
                    />
                    {" "}Foil Trend: {getDisplayFoilTrend(card)}
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                    <label style={{ fontSize: "10px", lineHeight: 1.35, whiteSpace: "nowrap", color: isWantTable ? "#0f766e" : "#14532d" }}>
                      <input
                        type="radio"
                        name={`priceMode-${card.id}`}
                        checked={listingMode === "custom"}
                        onChange={() => updateListingOption({ mode: "custom" })}
                      />
                      {" "}Custom
                    </label>
                    <div style={{ width: "72px", minWidth: "72px" }}>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={getCustomListingPrice(card, listingOptions)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => updateListingOption({ mode: "custom", customPrice: event.target.value })}
                        disabled={listingMode !== "custom"}
                        style={{
                          visibility: listingMode === "custom" ? "visible" : "hidden",
                          width: "72px",
                          minWidth: "72px",
                          boxSizing: "border-box",
                          padding: "4px 6px",
                          borderRadius: "6px",
                          border: "1px solid #86efac",
                          background: "white",
                          fontSize: "10px"
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #d7dce5",
        borderRadius: "12px",
        background: "white",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          minWidth: showSelectionIndicatorColumn ? "1120px" : "1080px",
        }}
      >
        <colgroup>
          {showSelectionIndicatorColumn ? <col style={{ width: "3%" }} /> : null}
          <col style={{ width: showSelectionIndicatorColumn ? (hasTradeLockColumn ? "4%" : "5%") : "4%" }} />
          <col style={{ width: showSelectionIndicatorColumn ? (hasTradeLockColumn ? "12%" : "13%") : (hasTradeLockColumn ? "13%" : "14%") }} />
          <col style={{ width: showSelectionIndicatorColumn ? (hasTradeLockColumn ? "16%" : "17%") : (hasTradeLockColumn ? "17%" : "18%") }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: hasTradeLockColumn ? "10%" : "12%" }} />
          <col style={{ width: hasTradeLockColumn ? "7%" : "8%" }} />
          <col style={{ width: hasTradeLockColumn ? "7%" : "8%" }} />
          {hasTradeLockColumn ? <col style={{ width: showSelectionIndicatorColumn ? "7%" : "7%" }} /> : null}
          <col style={{ width: showSelectionIndicatorColumn ? (hasTradeLockColumn ? "4%" : "4%") : (hasTradeLockColumn ? "5%" : "6%") }} />
        </colgroup>
        <thead>
          <tr style={{ background: "#f8fafc", textAlign: "left" }}>
            {showSelectionIndicatorColumn ? (
              <th style={{ padding: "10px 8px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }} aria-label="Selected" />
            ) : null}
            <th aria-sort={getAriaSort("qty")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Qty", "qty")}</th>
            <th aria-sort={getAriaSort("name")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Name", "name")}</th>
            <th aria-sort={getAriaSort("edition")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Edition", "edition")}</th>
            <th aria-sort={getAriaSort("language")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Language", "language")}</th>
            <th aria-sort={getAriaSort("condition")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Condition", "condition")}</th>
            <th aria-sort={getAriaSort("foil")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Foil?", "foil")}</th>
            <th aria-sort={getAriaSort("altered")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Altered?", "altered")}</th>
            <th aria-sort={getAriaSort("signed")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Signed?", "signed")}</th>
            <th aria-sort={getAriaSort("price")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader(priceColumnLabel, "price")}</th>
            <th aria-sort={getAriaSort("trend")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Trend", "trend")}</th>
            <th aria-sort={getAriaSort("foilTrend")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Foil Trend", "foilTrend")}</th>
            {hasTradeLockColumn ? <th style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>Status</th> : null}
            <th style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }} />
          </tr>
        </thead>
        <tbody>
          {sortedCards.map((card) => {
            const cardDisabled = Boolean(isCardDisabled?.(card));
            const cardName = getDisplayName(card);
            const isSelected = selectedCards?.includes(card.id);
            const resolvedBadgeType = typeof badgeType === "function" ? badgeType(card) : badgeType;
            const isUnlistedTable = resolvedBadgeType === "UNLISTED";
            const isListedTable = resolvedBadgeType === "LISTED";
            const isWantTable = resolvedBadgeType === "WANT";
            const isLockedListedCard = isListedTable && Boolean(card.isTradeLocked);
            const rowAction = renderRowAction ? renderRowAction({ card, cardDisabled }) : null;
            const rowBackground = isListedTable
              ? (isSelected ? "#dff3e3" : "#f2fbf4")
              : isWantTable
                ? (isSelected ? "#e6f4ea" : "#f8fcf9")
              : (isSelected ? "#e8f0fe" : "white");
            const showsListingControls = !editMode && isSelected && (isUnlistedTable || (isListedTable && !isLockedListedCard));
            const currentEdit = pendingEdits[card.id] || {};
            const printingsState = printingsByCardName[cardName] || { options: [], isLoading: false, error: "" };
            const resolvedEdit = getResolvedEditValues(card, currentEdit, printingsState.options);
            const isSavingEdit = Boolean(savingEditIds[card.id]);
            const selectedPrinting = printingsState.options.find((printing) => printing.scryfallId === resolvedEdit.scryfallId)
              || getMatchingPrinting(card, printingsState.options);
            const setSymbolColor = getRarityColor(selectedPrinting?.rarity || card.rarity);
            const cardSetCode = isWantTable && (resolvedEdit.anyEdition || card.any_edition || card.anyEdition)
              ? ""
              : normalizeLookupValue(selectedPrinting?.setCode || resolvedEdit.edition || card.edition);
            const cardSetMetadata = setMetadata[cardSetCode];
            const foilOptionContext = {
              selectedPrinting: selectedPrinting || card,
              printings: printingsState.options,
              anyEdition: isWantTable && resolvedEdit.anyEdition
            };
            const canChooseFoil = canChooseFoilOption({
              ...foilOptionContext,
              fallbackFoil: Boolean(card.foil)
            });
            const editionSelectValue = isWantTable && resolvedEdit.anyEdition
              ? WANTS_ANY_EDITION_VALUE
              : (resolvedEdit.scryfallId || CURRENT_EDITION_OPTION);
            const displayPrice = getDisplayPrice(card);
            const customPriceComparison = isListedTable
              ? getCustomPriceTrendComparison(card, displayPrice)
              : null;
            const listedPriceBadgeBackground = customPriceComparison?.tone === "warning" ? "#f97316" : "#4caf50";
            const listingMode = getListingMode(card, listingOptions);
            const currentEditionLabel = isWantTable && resolvedEdit.anyEdition
              ? "Any"
              : (selectedPrinting?.edition
              || cardSetMetadata?.name
              || getDisplayEdition(card));
            const secondaryEditionLabel = isWantTable && (resolvedEdit.anyEdition || card.any_edition || card.anyEdition)
              ? ""
              : (cardSetCode ? cardSetCode.toUpperCase() : getEditionCode(card));
            const editionOptions = [
              ...(isWantTable ? [{ value: WANTS_ANY_EDITION_VALUE, label: "Any" }] : []),
              ...(editionSelectValue === CURRENT_EDITION_OPTION ? [{
                value: CURRENT_EDITION_OPTION,
                label: `${currentEditionLabel}${cardSetCode ? ` (${cardSetCode.toUpperCase()})` : ""}`,
                setCode: cardSetCode,
                setMetadata: cardSetMetadata,
                symbolColor: setSymbolColor,
                showSetSymbol: Boolean(cardSetCode)
              }] : []),
              ...(printingsState.isLoading ? [{ value: "", label: "Loading editions...", disabled: true }] : []),
              ...(!printingsState.isLoading && printingsState.error ? [{ value: "__error__", label: printingsState.error, disabled: true }] : []),
              ...(!printingsState.isLoading ? printingsState.options.map((printing) => ({
                value: printing.scryfallId,
                label: `${printing.edition} (${printing.setCode.toUpperCase()})${printing.collectorNumber ? ` · #${printing.collectorNumber}` : ""}`,
                setCode: normalizeLookupValue(printing.setCode),
                setMetadata: setMetadata[normalizeLookupValue(printing.setCode)],
                symbolColor: getRarityColor(printing.rarity),
                showSetSymbol: Boolean(printing.setCode)
              })) : [])
            ];
            const languageSelectValue = isWantTable && resolvedEdit.anyLanguage ? WANTS_ANY_VALUE : resolvedEdit.language;
            const conditionSelectValue = isWantTable && resolvedEdit.anyCondition ? WANTS_ANY_VALUE : resolvedEdit.condition;
            const updatePendingEdit = (changes) => {
              if (!setPendingEdits) {
                return;
              }

              setPendingEdits((prev) => {
                const nextBase = getResolvedEditValues(card, prev[card.id] || {}, printingsState.options);

                return {
                  ...prev,
                  [card.id]: {
                    ...nextBase,
                    ...changes
                  }
                };
              });
            };
            const buildNextEdit = (changes) => {
              const nextBase = getResolvedEditValues(card, currentEdit, printingsState.options);

              return {
                ...nextBase,
                ...changes
              };
            };
            const applyImmediateEdit = async (changes) => {
              const nextEdit = buildNextEdit(changes);

              updatePendingEdit(changes);

              if (!onAutoSaveEdit) {
                return;
              }

              try {
                await onAutoSaveEdit(card.id, nextEdit);
              } catch (error) {
                await showAlert({
                  title: "Could Not Save Changes",
                  message: error.message || "Could not save card changes",
                  tone: "danger"
                });
              }
            };
            const updateListingOption = (changes) => {
              if (!setListingOptions) {
                return;
              }

              setListingOptions((prev) => ({
                ...prev,
                [card.id]: {
                  mode: getDefaultListingMode(card),
                  customPrice: "",
                  ...prev[card.id],
                  ...changes
                }
              }));
            };

            return (
              <tr
                key={card.id}
                onClick={usesModalEdit
                  ? (() => onEditCard(card))
                  : (editMode ? undefined : (onCardClick ? (() => !cardDisabled && onCardClick(card)) : (() => toggleSelect && toggleSelect(card.id))))}
                style={{
                  background: cardDisabled ? "#f8fafc" : rowBackground,
                  cursor: cardDisabled ? "not-allowed" : (usesModalEdit ? "pointer" : (editMode ? "default" : ((onCardClick || toggleSelect) ? "pointer" : "default"))),
                  opacity: cardDisabled ? 0.6 : 1,
                }}
              >
                {showSelectionIndicatorColumn ? (
                  <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef2f7", textAlign: "center", verticalAlign: "middle" }}>
                    {isSelected ? getSelectionBadge() : null}
                  </td>
                ) : null}
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontWeight: 600, fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <input
                      type="number"
                      min="1"
                      value={resolvedEdit.qty}
                      disabled={isSavingEdit || isLockedListedCard}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => updatePendingEdit({ qty: Math.max(1, Number(event.target.value) || 1) })}
                      onBlur={(event) => {
                        const nextQty = Math.max(1, Number(event.target.value) || 1);
                        void applyImmediateEdit({ qty: nextQty });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.currentTarget.blur();
                        }
                      }}
                      style={{
                        width: "100%",
                        minWidth: "56px",
                        fontSize: "12px",
                        padding: "6px 8px",
                        borderRadius: "6px",
                        border: "1px solid #d0d7e2",
                        background: "white",
                        boxSizing: "border-box"
                      }}
                    />
                  ) : (
                    resolveDisplayQty(card)
                  )}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{renderCardName(card)}</td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <div onClick={(event) => event.stopPropagation()}>
                      <AccountSettingSelect
                        title="Edition"
                        value={editionSelectValue}
                        onOpen={() => loadPrintingsForCardName(cardName)}
                        onChange={async (nextValue) => {
                          if (nextValue === CURRENT_EDITION_OPTION) {
                            return;
                          }

                          if (isWantTable && nextValue === WANTS_ANY_EDITION_VALUE) {
                            await applyImmediateEdit({ anyEdition: true });
                            return;
                          }

                          const nextPrinting = printingsState.options.find((printing) => printing.scryfallId === nextValue);

                          await applyImmediateEdit({
                            edition: nextPrinting?.setCode || resolvedEdit.edition,
                            scryfallId: nextPrinting?.scryfallId || "",
                            ...normalizeFoilSelection({ foil: nextPrinting?.foil ? resolvedEdit.foil : false, anyFoil: resolvedEdit.anyFoil }, {
                              selectedPrinting: nextPrinting,
                              printings: printingsState.options,
                              anyEdition: false
                            }),
                            anyEdition: false
                          });
                        }}
                        disabled={!cardName || cardName === "-" || isSavingEdit || isLockedListedCard}
                        variant="compact"
                        options={editionOptions}
                        renderSelectedValue={(value, option) => renderEditionOption(value, option, currentEditionLabel)}
                        renderOption={(value, option) => renderEditionOption(value, option)}
                      />
                    </div>
                  ) : (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                      {cardSetMetadata?.iconSvgMarkup && (
                        <span
                          aria-hidden="true"
                          style={{
                            width: "16px",
                            height: "16px",
                            flex: "0 0 auto",
                            color: setSymbolColor,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                          dangerouslySetInnerHTML={{ __html: cardSetMetadata.iconSvgMarkup }}
                        />
                      )}
                      <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.2 }}>
                        <span>{currentEditionLabel}</span>
                        {secondaryEditionLabel && <span style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase" }}>{secondaryEditionLabel}</span>}
                      </span>
                    </div>
                  )}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <div onClick={(event) => event.stopPropagation()}>
                      <AccountSettingSelect
                        title="Language"
                        value={languageSelectValue}
                        onChange={async (nextValue) => {
                          await applyImmediateEdit({
                            language: nextValue === WANTS_ANY_VALUE ? resolvedEdit.language : nextValue,
                            anyLanguage: isWantTable && nextValue === WANTS_ANY_VALUE
                          });
                        }}
                        disabled={isSavingEdit || isLockedListedCard}
                        variant="compact"
                        options={[
                          ...(isWantTable ? [{ value: WANTS_ANY_VALUE, label: "Any" }] : []),
                          { value: "", label: "Language" },
                          { value: "en", label: "English" },
                          { value: "de", label: "German" },
                          { value: "fr", label: "French" },
                          { value: "es", label: "Spanish" },
                          { value: "it", label: "Italian" },
                          { value: "jp", label: "Japanese" },
                          { value: "cn", label: "Chinese" },
                          { value: "kr", label: "Korean" },
                          { value: "ru", label: "Russian" },
                          { value: "pt", label: "Portuguese" }
                        ]}
                        renderSelectedValue={renderLanguageValue}
                        renderOption={renderLanguageValue}
                      />
                    </div>
                  ) : (
                    getDisplayLanguage(card)
                  )}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <div onClick={(event) => event.stopPropagation()}>
                      <AccountSettingSelect
                        title="Condition"
                        value={conditionSelectValue}
                        onChange={async (nextValue) => {
                          await applyImmediateEdit({
                            condition: nextValue === WANTS_ANY_VALUE ? resolvedEdit.condition : nextValue,
                            anyCondition: isWantTable && nextValue === WANTS_ANY_VALUE
                          });
                        }}
                        disabled={isSavingEdit || isLockedListedCard}
                        variant="compact"
                        options={[
                          ...(isWantTable ? [{ value: WANTS_ANY_VALUE, label: "Any" }] : []),
                          { value: "", label: "Condition" },
                          ...CONDITION_OPTIONS.map((option) => ({ value: option, label: option }))
                        ]}
                        renderSelectedValue={renderConditionValue}
                        renderOption={renderConditionValue}
                      />
                    </div>
                  ) : (
                    getDisplayCondition(card)
                  )}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <div onClick={(event) => event.stopPropagation()}>
                      <AccountSettingSelect
                        title="Foil"
                        value={isWantTable ? getWantBooleanSelectValue(resolvedEdit.foil, resolvedEdit.anyFoil) : getBooleanSelectValue(resolvedEdit.foil)}
                        onChange={async (nextValue) => {
                          if (isWantTable) {
                            const nextState = getWantBooleanFormState(nextValue);
                            await applyImmediateEdit(normalizeFoilSelection({ foil: nextState.boolValue, anyFoil: nextState.anyValue }, foilOptionContext));
                            return;
                          }

                          await applyImmediateEdit(normalizeFoilSelection({ foil: getBooleanFromSelectValue(nextValue), anyFoil: false }, foilOptionContext));
                        }}
                        disabled={(!canChooseFoil && !resolvedEdit.foil) || isSavingEdit || isLockedListedCard}
                        variant="compact"
                        options={(isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS).map((option) => ({
                          ...option,
                          disabled: (option.value === "true" && !canChooseFoil)
                            || isFoilOptionDisabled(option.value, foilOptionContext)
                        }))}
                        renderSelectedValue={(value) => renderBooleanValue(value, isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS)}
                        renderOption={(value) => renderBooleanValue(value, isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS)}
                      />
                    </div>
                  ) : getDisplayBoolean(card.foil, card.any_foil || card.anyFoil, "foil")}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <div onClick={(event) => event.stopPropagation()}>
                      <AccountSettingSelect
                        title="Altered"
                        value={isWantTable ? getWantBooleanSelectValue(resolvedEdit.altered, resolvedEdit.anyAltered) : getBooleanSelectValue(resolvedEdit.altered)}
                        onChange={async (nextValue) => {
                          if (isWantTable) {
                            const nextState = getWantBooleanFormState(nextValue);
                            await applyImmediateEdit({ altered: nextState.boolValue, anyAltered: nextState.anyValue });
                            return;
                          }

                          await applyImmediateEdit({ altered: getBooleanFromSelectValue(nextValue) });
                        }}
                        disabled={isSavingEdit || isLockedListedCard}
                        variant="compact"
                        options={isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS}
                        renderSelectedValue={(value) => renderBooleanValue(value, isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS)}
                        renderOption={(value) => renderBooleanValue(value, isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS)}
                      />
                    </div>
                  ) : getDisplayBoolean(card.altered, card.any_altered || card.anyAltered, "altered")}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  {editMode && !usesModalEdit ? (
                    <div onClick={(event) => event.stopPropagation()}>
                      <AccountSettingSelect
                        title="Signed"
                        value={isWantTable ? getWantBooleanSelectValue(resolvedEdit.signed, resolvedEdit.anySigned) : getBooleanSelectValue(resolvedEdit.signed)}
                        onChange={async (nextValue) => {
                          if (isWantTable) {
                            const nextState = getWantBooleanFormState(nextValue);
                            await applyImmediateEdit({ signed: nextState.boolValue, anySigned: nextState.anyValue });
                            return;
                          }

                          await applyImmediateEdit({ signed: getBooleanFromSelectValue(nextValue) });
                        }}
                        disabled={isSavingEdit || isLockedListedCard}
                        variant="compact"
                        options={isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS}
                        renderSelectedValue={(value) => renderBooleanValue(value, isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS)}
                        renderOption={(value) => renderBooleanValue(value, isWantTable ? WANTS_BOOLEAN_OPTIONS : BOOLEAN_OPTIONS)}
                      />
                    </div>
                  ) : getDisplayBoolean(card.signed, card.any_signed || card.anySigned, "signed")}
                </td>
                <td style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid #eef2f7",
                  fontSize: "12px",
                  verticalAlign: showsListingControls ? "top" : "middle",
                  background: isListedTable && showsListingControls ? "#dcf5df" : undefined
                }}>
                  {showsListingControls ? (
                      <div onClick={(event) => event.stopPropagation()}>
                        <div style={{ fontSize: "11px", lineHeight: 1.35, color: isWantTable ? "#0f766e" : "#14532d", fontWeight: 700, marginBottom: "4px" }}>
                          {pricePanelTitle}
                        </div>
                        <label style={{ display: "block", fontSize: "11px", lineHeight: 1.35, color: isWantTable ? "#0f766e" : "#14532d" }}>
                          <input
                            type="radio"
                            name={`priceMode-${card.id}`}
                            checked={listingMode === "trend"}
                            disabled={Boolean(card.foil)}
                            onChange={() => !card.foil && updateListingOption({ mode: "trend", customPrice: "" })}
                          />
                          {" "}Trend: {formatCurrency(card.price_trend)}
                        </label>
                        <label
                          style={{
                            display: "block",
                            marginTop: "4px",
                            fontSize: "11px",
                            lineHeight: 1.35,
                            color: card.foil && Number(card.price_foil) > 0 ? (isWantTable ? "#0f766e" : "#14532d") : "#6b7280"
                          }}
                        >
                          <input
                            type="radio"
                            name={`priceMode-${card.id}`}
                            checked={listingMode === "foiltrend"}
                            disabled={!card.foil || Number(card.price_foil) <= 0}
                            onChange={() =>
                              card.foil && Number(card.price_foil) > 0 && updateListingOption({ mode: "foiltrend", customPrice: "" })
                            }
                          />
                          {" "}Foil Trend: {getDisplayFoilTrend(card)}
                        </label>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                          <label style={{ fontSize: "11px", lineHeight: 1.35, whiteSpace: "nowrap", color: isWantTable ? "#0f766e" : "#14532d" }}>
                            <input
                              type="radio"
                              name={`priceMode-${card.id}`}
                              checked={listingMode === "custom"}
                              onChange={() => updateListingOption({ mode: "custom" })}
                            />
                            {" "}Custom
                          </label>
                          <div style={{ width: "72px", minWidth: "72px" }}>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="€"
                              value={getCustomListingPrice(card, listingOptions)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => updateListingOption({ mode: "custom", customPrice: event.target.value })}
                              disabled={listingMode !== "custom"}
                              style={{
                                visibility: listingMode === "custom" ? "visible" : "hidden",
                                width: "72px",
                                minWidth: "72px",
                                boxSizing: "border-box",
                                padding: "4px 6px",
                                borderRadius: "6px",
                                border: "1px solid #86efac",
                                background: "white",
                                fontSize: "11px"
                              }}
                            />
                          </div>
                        </div>
                      </div>
                  ) : (isUnlistedTable || isWantTable) && !editMode ? (
                    isWantTable && displayPrice !== null ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "4px",
                          padding: "3px 8px",
                          borderRadius: "999px",
                          background: "#4caf50",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: 700,
                          lineHeight: 1.35
                        }}
                      >
                        <span>{formatCurrency(displayPrice)}</span>
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: "11px" }}>{emptyPriceCellLabel}</span>
                    )
                  ) : (
                    displayPrice !== null ? (
                      <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "3px 8px",
                            borderRadius: "999px",
                            background: listedPriceBadgeBackground,
                            color: "white",
                            fontSize: "11px",
                            fontWeight: 700,
                            lineHeight: 1.35
                          }}
                        >
                          {formatCurrency(displayPrice)}
                        </span>
                        {customPriceComparison ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "3px 8px",
                              borderRadius: "999px",
                              background: listedPriceBadgeBackground,
                              color: "white",
                              fontSize: "11px",
                              fontWeight: 700,
                              lineHeight: 1.35
                            }}
                          >
                            {formatCustomPriceTrendComparison(customPriceComparison.percentDelta)}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      formatCurrency(displayPrice)
                    )
                  )}
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{formatCurrency(card.price_trend)}</td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{getDisplayFoilTrend(card)}</td>
                {hasTradeLockColumn ? (
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                    {card.tradeLockLabel ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "3px 8px",
                          borderRadius: "999px",
                          background: card.isPendingTradeLocked ? "#fff7ed" : "#eff6ff",
                          border: card.isPendingTradeLocked ? "1px solid #fdba74" : "1px solid #93c5fd",
                          color: card.isPendingTradeLocked ? "#9a3412" : "#1d4ed8",
                          fontSize: "10px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {card.tradeLockLabel}
                      </span>
                    ) : null}
                  </td>
                ) : null}
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px", textAlign: "center" }}>
                  <div style={{ display: "grid", justifyItems: "center", gap: "6px" }}>
                    {rowAction}
                    {!showSelectionIndicatorColumn && !editMode && isSelected ? getSelectionBadge() : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}